import { HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, select, Store } from '@ngrx/store';

import { of } from 'rxjs';
import {
    catchError, concatMap, exhaustMap, filter, map, switchMap, withLatestFrom
} from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { DeviceGroupSelectors } from 'src/app/features/device-group/store/selectors';
import { NotificationsActions, NotificationType } from 'src/app/features/notifications';
import { ExceptionType } from 'src/app/features/notifications/models';
import { PackagesSelectors } from 'src/app/features/packages';
import { WizardSelectors } from 'src/app/features/wizard/store/selectors';
import { RootStoreState } from 'src/app/store';

import { PackagesActions } from '../../../packages/store/actions';
import { PresignConsentsService } from '../../services';
import { ConsentErrorKeys, mapValidationErrorsToActions } from '../../utils/consents.util';
import { ConsentsActions, PresignConsentsActions } from '../actions';
import { ConsentsSelectors } from '../selectors';

@Injectable()
export class PresignConsentsEffects {
  private readonly preSignConsentTextIdToAction: Record<string, Action<any>> = {
    [ConsentErrorKeys.TermsOfUseConsentTextId]: PresignConsentsActions.FetchTermsAndConditionsConsent(),
    [ConsentErrorKeys.PrivacyPolicyConsentTextId]: PresignConsentsActions.FetchPrivacyPolicyConsent(),
    [ConsentErrorKeys.AiDisclaimerConsentTextId]: PresignConsentsActions.FetchUseOfAIConsent(),
  };

  acceptTermsOfService$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresignConsentsActions.AcceptTermsOfService),
      concatLatestFrom(() => [this.store.select(ConsentsSelectors.selectAreTermsOfServiceConsentsLoaded)]),
      filter(([_, areAllConsentsLoaded]) => areAllConsentsLoaded),
      concatLatestFrom(() => [
        this.store.select(WizardSelectors.getActiveWizardUserGuid),
        this.store.select(ConsentsSelectors.getTermsAndConditionsConsent),
        this.store.select(ConsentsSelectors.getPrivacyPolicyConsent),
        this.store.select(DeviceGroupSelectors.getDeviceCode),
        this.store.select(ConsentsSelectors.getUseOfAIConsent),
      ]),
      exhaustMap(
        ([
          _,
          packageUserGuid,
          termsAndConditionsConsent,
          privacyPolicyConsent,
          deviceCode,
          useOfAIConsent,
        ]) => {
          return this.presignConsentsService
            .acceptTermsOfService({
              termsOfUseConsentTextId: termsAndConditionsConsent.consentTextId,
              privacyPolicyConsentTextId: privacyPolicyConsent.consentTextId,
              packageUserGuid: packageUserGuid,
              deviceCode,
              aiDisclaimerConsentTextId: useOfAIConsent?.consentTextId ?? null,
            })
            .pipe(
              map(() => ConsentsActions.AcceptTermsOfServiceSuccess()),
              catchError((err) =>
                of(
                  PresignConsentsActions.TermsOfServiceSubmissionFailed({
                    response: err,
                    notificationText: 'Failed to accept presign terms of service.',
                  })
                )
              )
            );
        }
      )
    )
  );

  declineTermsOfService$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresignConsentsActions.DeclineTermsOfService),
      concatLatestFrom(() => [this.store.select(ConsentsSelectors.selectAreTermsOfServiceConsentsLoaded)]),
      filter(([_, areAllConsentsLoaded]) => areAllConsentsLoaded),
      concatLatestFrom(() => [
        this.store.select(WizardSelectors.getActiveWizardUserGuid),
        this.store.select(ConsentsSelectors.getTermsAndConditionsConsent),
        this.store.select(ConsentsSelectors.getPrivacyPolicyConsent),
        this.store.select(DeviceGroupSelectors.getDeviceCode),
        this.store.select(ConsentsSelectors.getUseOfAIConsent),
      ]),
      exhaustMap(
        ([
          _,
          packageUserGuid,
          termsAndConditionsConsent,
          privacyPolicyConsent,
          deviceCode,
          useOfAIConsent,
        ]) => {
          return this.presignConsentsService
            .declineTermsOfService({
              termsOfUseConsentTextId: termsAndConditionsConsent.consentTextId,
              privacyPolicyConsentTextId: privacyPolicyConsent.consentTextId,
              packageUserGuid: packageUserGuid,
              deviceCode,
              aiDisclaimerConsentTextId: useOfAIConsent?.consentTextId ?? null,
            })
            .pipe(
              map(() =>
                PackagesActions.OptOutPackage({
                  payload: {
                    categoryTypeCode: 'ParticipantConsentDecline',
                    comments: 'Participant declined terms of service',
                  },
                })
              ),
              catchError((err) =>
                of(
                  PresignConsentsActions.TermsOfServiceSubmissionFailed({
                    response: err,
                    notificationText: 'Failed to decline presign terms of service.',
                  })
                )
              )
            );
        }
      )
    )
  );

  termsOfServiceConsentNotLoadedBeforeSubmission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresignConsentsActions.AcceptTermsOfService, PresignConsentsActions.DeclineTermsOfService),
      concatLatestFrom(() => [this.store.select(ConsentsSelectors.selectAreTermsOfServiceConsentsLoaded)]),
      filter(([_, areAllConsentsLoaded]) => !areAllConsentsLoaded),
      concatLatestFrom(() => [
        this.store.select(ConsentsSelectors.getTermsAndConditionsConsent),
        this.store.select(ConsentsSelectors.getPrivacyPolicyConsent),
        this.store.select(ConsentsSelectors.selectIsUseOfAiConsentValid)
      ]),
      switchMap(([_, termsOfUse, privacyPolicy, isUseOfAiValid]) => {
        const missingConsentActions = [
          !termsOfUse && PresignConsentsActions.FetchTermsAndConditionsConsent(),
          !privacyPolicy && PresignConsentsActions.FetchPrivacyPolicyConsent(),
          !isUseOfAiValid && PresignConsentsActions.FetchUseOfAIConsent(),
        ].filter(action => !!action);

        return [
          NotificationsActions.AddNotification({
            payload: {
              exceptionType: ExceptionType.DisplayMessage,
              notificationType: NotificationType.Info,
              id: uuid(),
              text: 'Please wait while we load missing consents, then try again.',
              logInAppInsights: true,
              options: {
                closeButton: true,
                tapToDismiss: true,
                timeOut: 5000,
              },
            },
          }),
          ...missingConsentActions,
        ];
      })
    )
  );

  getPrivacyPolicysConsent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresignConsentsActions.FetchPrivacyPolicyConsent),
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.pipe(select(WizardSelectors.getActiveWizardUserGuid)),
            this.store.pipe(select(DeviceGroupSelectors.getDeviceCode))
          )
        )
      ),
      switchMap(([_, packageUserGuid, deviceCode]) => {
        return this.presignConsentsService.getPrivacyConsent(packageUserGuid, deviceCode).pipe(
          map((payload) => ConsentsActions.SetPrivacyPolicyConsent({ payload })),
          catchError((err) =>
            of(
              NotificationsActions.AddNotification({
                payload: {
                  exception: err,
                  notificationType: NotificationType.Error,
                  id: uuid(),
                  text: 'Failed to get privacy policy.',
                  exceptionType: ExceptionType.CannotProceed,
                },
              })
            )
          )
        );
      })
    )
  );

  getUseOfAIConsent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresignConsentsActions.FetchUseOfAIConsent),
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.select(WizardSelectors.getActiveWizardUserGuid),
            this.store.select(DeviceGroupSelectors.getDeviceCode),
            this.store.select(PackagesSelectors.isRONOrKRON)
          )
        )
      ),
      filter(([_, __, ___, isRONOrKRON]) => isRONOrKRON),
      switchMap(([_, packageUserGuid, deviceCode, __]) => {
        return this.presignConsentsService.getUseOfAIConsent(packageUserGuid, deviceCode).pipe(
          map((payload) => ConsentsActions.SetUseOfAIConsent({ payload })),
          catchError((err) =>
            of(
              NotificationsActions.AddNotification({
                payload: {
                  exception: err,
                  notificationType: NotificationType.Error,
                  id: uuid(),
                  text: 'Failed to get use of ai.',
                  exceptionType: ExceptionType.CannotProceed,
                },
              })
            )
          )
        );
      })
    )
  );

  getTermsAndConditionsConsent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresignConsentsActions.FetchTermsAndConditionsConsent),
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.pipe(select(WizardSelectors.getActiveWizardUserGuid)),
            this.store.pipe(select(DeviceGroupSelectors.getDeviceCode))
          )
        )
      ),
      switchMap(([_, packageUserGuid, deviceCode]) => {
        return this.presignConsentsService
          .getTermsAndConditionsConsent(packageUserGuid, deviceCode)
          .pipe(
            map((payload) => ConsentsActions.SetTermsAndConditionsConsent({ payload })),
            catchError((err) =>
              of(
                NotificationsActions.AddNotification({
                  payload: {
                    exception: err,
                    notificationType: NotificationType.Error,
                    id: uuid(),
                    text: 'Failed to get terms and conditions.',
                    exceptionType: ExceptionType.CannotProceed,
                  },
                })
              )
            )
          );
      })
    )
  );

  getEndorsementConsent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresignConsentsActions.FetchEndorsementsConsent),
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.pipe(select(WizardSelectors.getActiveWizardUserGuid)),
            this.store.pipe(select(DeviceGroupSelectors.getDeviceCode))
          )
        )
      ),
      switchMap(([_, packageUserGuid, deviceCode]) => {
        return this.presignConsentsService.getEndorsementsConsent(packageUserGuid, deviceCode).pipe(
          map((payload) => ConsentsActions.SetEndorsementsConsent({ payload })),
          catchError((err) =>
            of(
              NotificationsActions.AddNotification({
                payload: {
                  exception: err,
                  notificationType: NotificationType.Error,
                  id: uuid(),
                  text: 'Failed to get endorsement consent.',
                  exceptionType: ExceptionType.CannotProceed,
                },
              })
            )
          )
        );
      })
    )
  );

  fetchAllTermsOfServiceConsents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresignConsentsActions.FetchAllTermsOfServiceConsents),
      switchMap(() => [
        PresignConsentsActions.FetchTermsAndConditionsConsent(),
        PresignConsentsActions.FetchPrivacyPolicyConsent(),
        PresignConsentsActions.FetchUseOfAIConsent(),
      ])
    )
  );

  termsOfServiceConsentInvalid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresignConsentsActions.TermsOfServiceConsentInvalid),
      switchMap(() => [
        PresignConsentsActions.FetchAllTermsOfServiceConsents(),
        NotificationsActions.AddNotification({
          payload: {
            exceptionType: ExceptionType.DisplayMessage,
            notificationType: NotificationType.Info,
            id: uuid(),
            text: 'Terms of service have been updated. Please review and accept the updated terms to continue.',
            logInAppInsights: true,
            options: {
              closeButton: true,
              tapToDismiss: true,
              timeOut: 5000,
            },
          },
        }),
      ])
    )
  );

  termsOfServiceSubmissionFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresignConsentsActions.TermsOfServiceSubmissionFailed),
      switchMap((action) => {
        const response = action.response;

        const defaultActions: Action[] = [
          NotificationsActions.AddNotification({
            payload: {
              exception: response,
              notificationType: NotificationType.Error,
              id: uuid(),
              text: action.notificationText,
              exceptionType: ExceptionType.CannotProceed,
            },
          }),
        ];

        if (response.status === HttpStatusCode.Conflict) {
          return [ConsentsActions.TermsOfServiceConsentInvalid()];
        }

        if (response.status === HttpStatusCode.BadRequest) {
          const actions = mapValidationErrorsToActions(
            response.error,
            this.preSignConsentTextIdToAction
          );
          if (actions.length > 0) {
            actions.push(...defaultActions);
            return actions;
          }
        }

        return defaultActions;
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly presignConsentsService: PresignConsentsService,
    private readonly store: Store<RootStoreState.State>
  ) {}
}
